import { ArrayToLookupByPredicate } from "global/Utility/TSFunctions";
import { IAccountsReceivable, IBenefit, IFeesAndPremiums, IGroupConfigurationData, IPlan } from "model/FlexibleAttributeModel";
import { BaseJsonTransformer } from "./IJsonTransformer";
import * as FAM from "model/FlexibleAttributeModel";

/**
 * 
 * Transformer:
 * https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/412925
 * This is to prevent the % field from being nulled by the new parent value.
 * This should run BEFORE the visibility rules so that the % field is not nulled before it runs.
 * 
 * Fees and Premiums - General Tab - EFFECTIVE DATES - Consolidate Medical Fields
 * https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/374576
 * 1) Identify the value in the following date fields:
 * Build: Attribute: Information: PPO Effective Date - Dynamics 365
 * Build: Attribute: Information: EPO Effective Date - Dynamics 365
 * Build: Attribute: Information: Comp Effective Date - Dynamics 365
 * 2) Identify which of the above is the earliest (smallest value)
 * 3) Populate the identified value in the following field: Build: Attribute: Information: Medical Effective Date - Dynamics 365
 */
export class Sprint18Transformer extends BaseJsonTransformer {

    benefits: IBenefit[] = [];
    feesAndPremiums: IFeesAndPremiums[] = [];

    constructor() {
        super();
    }

    async before(json: IGroupConfigurationData): Promise<void> {
        console.debug("[tran] Sprint18Transformer.before")

        this.benefits = json.benefits;
        this.feesAndPremiums = json.feesAndPremiums;
    }

    async transformAccountsReceivable(accountsReceivable: IAccountsReceivable): Promise<boolean | void> {

        const feesAndPremiumFieldMap = ArrayToLookupByPredicate(accountsReceivable.fields, (t) => {
            return t.configRef.id
        });

        // Termination Fees will be paid at % of claim(Enter%)
        // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=42d1a630-3c14-ed11-b83b-000d3a372023&pagetype=entityrecord&etn=bcbs_config_attribute&id=c2d36d78-b28d-ec11-b400-0022480a93ed
        const bcbs_fin_term_fee_MG_per_of_claim = feesAndPremiumFieldMap["c2d36d78-b28d-ec11-b400-0022480a93ed"];

        // Term Fee Percentage Designation
        // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=42d1a630-3c14-ed11-b83b-000d3a372023&newWindow=true&pagetype=entityrecord&etn=bcbs_config_attribute&id=9b9ac160-7a26-ee11-9966-00224808116e
        const bcbs_fin_term_fee_designation = feesAndPremiumFieldMap["9b9ac160-7a26-ee11-9966-00224808116e"];


        const bcbs_fin_term_fee_MG_per_of_claim_value = bcbs_fin_term_fee_MG_per_of_claim?.values?.[0]?.value

        if (bcbs_fin_term_fee_MG_per_of_claim_value && bcbs_fin_term_fee_MG_per_of_claim_value.type === 'number')
            if (bcbs_fin_term_fee_MG_per_of_claim_value.value > 0) {
                bcbs_fin_term_fee_designation.values[0].value = {
                    type: 'bool',
                    value: true
                }
            }

    }

    async transformFeesAndPremium(feesAndPremium: IFeesAndPremiums): Promise<boolean | void> {

        const feesAndPremiumFieldMap = ArrayToLookupByPredicate(feesAndPremium.fields, (t) => {
            return t.configRef.id
        });

        // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=7bd0a630-3c14-ed11-b83b-000d3a372023&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=50eab0ef-e778-ec11-8d21-000d3a35957e
        const compEffectiveDate = feesAndPremiumFieldMap["50eab0ef-e778-ec11-8d21-000d3a35957e"];

        // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=7bd0a630-3c14-ed11-b83b-000d3a372023&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=48eab0ef-e778-ec11-8d21-000d3a35957e
        const epoEffectiveDate = feesAndPremiumFieldMap["48eab0ef-e778-ec11-8d21-000d3a35957e"];

        // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=7bd0a630-3c14-ed11-b83b-000d3a372023&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=4aeab0ef-e778-ec11-8d21-000d3a35957e
        const ppoEffectiveDate = feesAndPremiumFieldMap["4aeab0ef-e778-ec11-8d21-000d3a35957e"];

        // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=7bd0a630-3c14-ed11-b83b-000d3a372023&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=8770b9ab-9b0f-ee11-8f6d-000d3a305861
        const medEffectiveDate = feesAndPremiumFieldMap["8770b9ab-9b0f-ee11-8f6d-000d3a305861"];

        const dates: string[] = [];

        if (compEffectiveDate.values[0].value?.type === 'date')
            dates.push(compEffectiveDate.values[0].value.value);

        if (epoEffectiveDate.values[0].value?.type === 'date')
            dates.push(epoEffectiveDate.values[0].value.value);

        if (ppoEffectiveDate.values[0].value?.type === 'date')
            dates.push(ppoEffectiveDate.values[0].value.value);


        const minDatePredicate = (a: string, b: string) => {
            return new Date(a) < new Date(b) ? a : b;
        }

        if (dates.length > 0) {
            const date = dates.reduce(minDatePredicate);
            if (date && !medEffectiveDate.values[0].value)
                medEffectiveDate.values[0].value = {
                    type: 'date',
                    value: date,
                }
        }
    }


    async transformPlan(plan: IPlan): Promise<boolean | void> {

        // debugger

        // // const medicalBenefit = this.benefits
        // //     .filter(b => b.bcbs_config_categoryid === '42ad9a7f-d63c-ec11-b6e5-0022480a9561'
        // //         && plan.benefitRefs.some(bRef => bRef.id === b.id)
        // //     );

        // const feesAndPremium = this.feesAndPremiums?.filter(fnp => fnp.id === plan.feesAndPremiumsRef?.id)?.[0];


    }

    async after(json: FAM.IGroupConfigurationData): Promise<void> {
        console.debug("[tran] Sprint18Transformer.after")

        if (json.transformers)
            json.transformers.Sprint18 = true
    }

}
